import { ErrorCode } from "@spare-cart/contracts/common";
import type { ErrorBody, ErrorDetail } from "@spare-cart/contracts/common";

export const NETWORK_STATUS = 0;

export class ApiError extends Error {
  override readonly name = "ApiError";
  readonly code: ErrorCode;
  readonly status: number;
  readonly requestId: string;
  readonly details: readonly ErrorDetail[];

  constructor(status: number, body: ErrorBody) {
    super(body.message);
    this.code = body.code;
    this.status = status;
    this.requestId = body.requestId;
    this.details = body.details ?? [];
  }
}

const STATUS_CODES: Readonly<Record<number, ErrorCode>> = {
  400: ErrorCode.VALIDATION_ERROR,
  401: ErrorCode.UNAUTHORIZED,
  403: ErrorCode.FORBIDDEN,
  404: ErrorCode.NOT_FOUND,
  409: ErrorCode.CONFLICT,
  422: ErrorCode.VALIDATION_ERROR,
  429: ErrorCode.RATE_LIMITED,
  402: ErrorCode.PAYMENT_FAILED,
};

export const codeForStatus = (status: number): ErrorCode =>
  STATUS_CODES[status] ?? ErrorCode.INTERNAL_ERROR;

const isErrorBody = (value: unknown): value is ErrorBody =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as { code?: unknown }).code === "string" &&
  typeof (value as { message?: unknown }).message === "string";

export const toApiError = (status: number, payload: unknown): ApiError => {
  const envelope = payload as { error?: unknown } | null | undefined;
  const body = envelope?.error;
  if (isErrorBody(body)) return new ApiError(status, body);

  return new ApiError(status, {
    code: codeForStatus(status),
    message:
      status === NETWORK_STATUS
        ? "The API could not be reached. Check your connection and try again."
        : "The API returned a response this client could not read.",
    requestId: "unavailable",
  });
};

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError;

export const hasCode = (error: unknown, code: ErrorCode): boolean =>
  isApiError(error) && error.code === code;

export const isOffline = (error: unknown): boolean =>
  isApiError(error) && error.status === NETWORK_STATUS;

export const fieldErrors = (error: unknown): Readonly<Record<string, string>> => {
  if (!isApiError(error)) return {};
  const map: Record<string, string> = {};
  for (const detail of error.details) map[detail.path] = detail.message;
  return map;
};
